'use client';
import Link from 'next/link';
// Note: SEO meta tags should be handled by generateMetadata in page.tsx (App Router pattern)
// next/head is NOT compatible with Next.js App Router
import Image from 'next/image';
import Breadcrumbs, { type BreadcrumbItem } from './Breadcrumbs';
import FAQSection, { type FAQItem } from './FAQSection';
import EngagementTracker from './EngagementTracker';
import TrustBadges from './TrustBadges';
import StickyMobileCTA from './StickyMobileCTA';
import MiniContactForm from './MiniContactForm';
import ScrollReveal from './ScrollReveal';
import { useHeadingIds } from '@/hooks/useHeadingIds';
import { buildContactUrl } from '@/lib/utils/contact-url';
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import { isCloudflareImageUrl, cfImage, cfImageSrcSet, SIZES } from '@/lib/cloudflare-images';
interface ServiceFeature {
    icon?: ReactNode;
    iconBackground?: string;
    iconClassName?: string;
    title: string;
    description: ReactNode;
}
interface ServiceBenefit {
    title: string;
    description: string;
}
interface ServiceCTA {
    title: string;
    description: string;
    primaryButton?: {
        text: string;
        href: string;
        className?: string;
    };
    secondaryButton?: {
        text: string;
        href: string;
        className?: string;
    };
}
interface HeroButton {
    text: string;
    href: string;
    className?: string;
}
interface HeroButtons {
    primary?: HeroButton;
    secondary?: HeroButton;
}
interface HeroTrustBadge {
    label: string;
    icon?: string;
}
interface RelatedService {
    name: string;
    slug: string;
    description: string;
    category?: string;
    icon?: string;
}
interface ServiceLayoutProps {
    serviceName: string;
    serviceSlug: string;
    categorySlug?: string; // Optional category for organization
    serviceDescription?: string;
    tagline?: string; // Short tagline for hero section
    // Hero section
    heroImage?: string; // Background image for hero
    heroBackgroundColor?: string; // Default gradient if no image
    heroAlign?: 'left' | 'center';
    heroTitle?: string;
    heroSubtitle?: string;
    heroDescription?: string;
    heroButtons?: HeroButtons;
    heroTrustBadges?: HeroTrustBadge[];
    heroTrustLabel?: string;
    heroTrustTrailingText?: string;
    heroOverlayOpacity?: number;
    // Features & Benefits
    features?: ServiceFeature[];
    benefits?: ServiceBenefit[];
    // FAQ
    faqItems?: FAQItem[];
    // CTA Section
    ctaSection?: ServiceCTA;
    showCTA?: boolean; // Default: true
    showHero?: boolean; // Default: true
    // Related Services
    relatedServices?: RelatedService[];
    showRelatedServices?: boolean; // Default: true
    // SEO
    keywords?: string[];
    ogImage?: string;
    // Custom sections
    beforeContent?: ReactNode; // Content before main children
    afterContent?: ReactNode; // Content after main children
    featuresSectionClassName?: string;
    // Embedded contact form
    showEmbeddedContactForm?: boolean; // Default: false
    embeddedContactFormProps?: {
        title?: string;
        description?: string;
        buttonText?: string;
    };
    // Main content
    children: ReactNode;
}
export default function ServiceLayout({ serviceName, serviceSlug, categorySlug, serviceDescription, tagline, heroImage, heroBackgroundColor = 'from-[#2cc3d1] to-[#2813e8]', heroAlign = 'left', heroTitle, heroSubtitle, heroDescription, heroButtons, heroTrustBadges, heroTrustLabel, heroTrustTrailingText, heroOverlayOpacity = 0.5, features, benefits, faqItems, ctaSection, showCTA = true, showHero = true, relatedServices, showRelatedServices = true, keywords, ogImage, beforeContent, afterContent, children, featuresSectionClassName, showEmbeddedContactForm = false, embeddedContactFormProps }: ServiceLayoutProps) {
    // Add IDs to all headings for deep linking
    useHeadingIds();
    // SEO metadata
    const siteUrl = 'https://inventivehq.com';
    const canonicalUrl = categorySlug
        ? `${siteUrl}/services/${categorySlug}/${serviceSlug}`
        : `${siteUrl}/services/${serviceSlug}`;
    const pageTitle = `${serviceName} | Inventive HQ`;
    const description = serviceDescription || tagline || `Expert ${serviceName.toLowerCase()} services from Inventive HQ. Transform your business with our cloud solutions and cybersecurity expertise.`;
    const finalOgImage = ogImage || `${siteUrl}/og-images/service-${serviceSlug}.png`;
    const heroHeading = heroTitle ?? serviceName;
    const heroSubheading = heroSubtitle ?? tagline;
    const heroBody = heroDescription ?? serviceDescription;
    const heroContainerClasses = `max-w-4xl${heroAlign === 'center' ? ' mx-auto text-center' : ''}`;
    const heroBodyClasses = `text-lg opacity-80 max-w-3xl${heroAlign === 'center' ? ' mx-auto' : ''}`;
    const heroButtonsClasses = `mt-8 flex flex-col sm:flex-row gap-4${heroAlign === 'center' ? ' items-center sm:justify-center' : ''}`;
    const heroButtonBaseClasses = "inline-block px-8 py-4 rounded-md font-semibold transition-all active:scale-95";
    const heroPrimaryDefaultClasses = `${heroButtonBaseClasses} bg-white dark:bg-gray-800 text-[#2813e8] hover:bg-gray-100 dark:hover:bg-gray-800`;
    const heroSecondaryDefaultClasses = `${heroButtonBaseClasses} bg-transparent border-2 border-white text-white hover:bg-white dark:hover:bg-gray-800 hover:text-[#2813e8]`;
    const ctaButtonBaseClasses = "inline-block px-8 py-4 rounded-md font-semibold transition-all active:scale-95";
    const ctaPrimaryDefaultClasses = `${ctaButtonBaseClasses} bg-white dark:bg-gray-800 text-[#2813e8] hover:bg-gray-100 dark:hover:bg-gray-800`;
    const ctaSecondaryDefaultClasses = `${ctaButtonBaseClasses} bg-transparent border-2 border-white text-white hover:bg-white dark:hover:bg-gray-800 hover:text-[#2813e8]`;
    const heroTrustLayoutClasses = heroAlign === 'center'
        ? 'flex flex-nowrap items-center justify-center gap-4 text-white overflow-x-auto'
        : 'flex flex-wrap items-center justify-between gap-4 text-white';
    // Breadcrumbs
    const breadcrumbs: BreadcrumbItem[] = [
        { label: 'Services', href: '/services' },
    ];
    if (categorySlug) {
        breadcrumbs.push({ label: categorySlug, href: `/services/${categorySlug}` });
    }
    breadcrumbs.push({ label: serviceName });
    // Build personalized contact URL
    const serviceContactUrl = buildContactUrl({
        src: 'service',
        id: serviceSlug,
        cat: categorySlug,
    });

    // Default CTA if not provided
    const defaultCTA: ServiceCTA = {
        title: `Get a Free ${serviceName} Assessment`,
        description: "No obligation, no pressure. See exactly how we can help protect and optimize your business.",
        primaryButton: {
            text: "Get Free Assessment",
            href: serviceContactUrl
        },
        secondaryButton: {
            text: "Call Us: (866) 903-2097",
            href: "tel:+18669032097"
        }
    };
    const finalCTA = ctaSection || defaultCTA;
    const sanitizedServiceSlug = serviceSlug.replace(/[^\w-]+/g, '-');
    const contactHrefWithTracking = (href: string, ctaId: string): string => {
        if (!href.startsWith('/contact')) {
            return href;
        }
        try {
            const url = new URL(href, 'https://inventivehq.com');
            if (!url.searchParams.has('ref')) {
                url.searchParams.set('ref', `service-${sanitizedServiceSlug}`);
            }
            if (!url.searchParams.has('cta')) {
                url.searchParams.set('cta', ctaId);
            }
            return `${url.pathname}${url.search}`;
        }
        catch {
            return href;
        }
    };
    const heroPrimaryHref = heroButtons?.primary ? contactHrefWithTracking(heroButtons.primary.href, 'hero-primary') : undefined;
    const heroSecondaryHref = heroButtons?.secondary ? contactHrefWithTracking(heroButtons.secondary.href, 'hero-secondary') : undefined;
    const ctaPrimaryHref = finalCTA.primaryButton ? contactHrefWithTracking(finalCTA.primaryButton.href, 'cta-primary') : undefined;
    const ctaSecondaryHref = finalCTA.secondaryButton ? contactHrefWithTracking(finalCTA.secondaryButton.href, 'cta-secondary') : undefined;
    const toAbsoluteUrl = (url?: string) => {
        if (!url)
            return undefined;
        if (url.startsWith('http'))
            return url;
        return `${siteUrl}${url.startsWith('/') ? url : `/${url}`}`;
    };
    const organizationId = `${siteUrl}#organization`;
    const websiteId = `${siteUrl}#website`;
    const webPageId = `${canonicalUrl}#webpage`;
    const serviceId = `${canonicalUrl}#service`;
    // Note: breadcrumbId removed - BreadcrumbList is now rendered by the Breadcrumbs component
    const featureListId = `${canonicalUrl}#features`;
    const benefitListId = `${canonicalUrl}#benefits`;
    const relatedServicesId = `${canonicalUrl}#related-services`;
    const faqId = `${canonicalUrl}#faq`;
    const heroImageUrl = toAbsoluteUrl(heroImage);
    const heroPrimaryUrl = toAbsoluteUrl(heroPrimaryHref);
    const heroSecondaryUrl = toAbsoluteUrl(heroSecondaryHref);
    const ctaPrimaryUrl = toAbsoluteUrl(ctaPrimaryHref);
    const ctaSecondaryUrl = toAbsoluteUrl(ctaSecondaryHref);
    const featureListElements = (features ?? []).map((feature, index) => {
        if (!feature?.title) {
            return null;
        }
        const item: Record<string, unknown> = {
            "@type": "ListItem",
            "position": index + 1,
            "name": feature.title
        };
        if (typeof feature.description === 'string') {
            item["description"] = feature.description;
        }
        return item;
    }).filter(Boolean);
    const featureItemList = featureListElements.length > 0 ? {
        "@type": "ItemList",
        "@id": featureListId,
        "name": `${serviceName} Key Features`,
        "itemListElement": featureListElements
    } : undefined;
    const benefitListElements = (benefits ?? []).map((benefit, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": benefit.title,
        "description": benefit.description
    }));
    const benefitItemList = benefitListElements.length > 0 ? {
        "@type": "ItemList",
        "@id": benefitListId,
        "name": `${serviceName} Benefits`,
        "itemListElement": benefitListElements
    } : undefined;
    const relatedServiceElements = (relatedServices ?? []).map((related, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
            "@type": "Service",
            "name": related.name,
            "description": related.description,
            "url": toAbsoluteUrl(related.slug ? `/services/${related.slug}` : undefined) ?? `${siteUrl}/services`
        }
    }));
    const relatedServicesList = relatedServiceElements.length > 0 ? {
        "@type": "ItemList",
        "@id": relatedServicesId,
        "name": "Related Services",
        "itemListElement": relatedServiceElements
    } : undefined;
    const potentialActions = [
        heroPrimaryUrl && heroButtons?.primary ? {
            "@type": "Action",
            "name": heroButtons.primary.text,
            "target": heroPrimaryUrl
        } : null,
        heroSecondaryUrl && heroButtons?.secondary ? {
            "@type": "Action",
            "name": heroButtons.secondary.text,
            "target": heroSecondaryUrl
        } : null,
        ctaPrimaryUrl && finalCTA.primaryButton ? {
            "@type": "Action",
            "name": finalCTA.primaryButton.text,
            "target": ctaPrimaryUrl
        } : null,
        ctaSecondaryUrl && finalCTA.secondaryButton ? {
            "@type": "Action",
            "name": finalCTA.secondaryButton.text,
            "target": ctaSecondaryUrl
        } : null
    ].filter(Boolean);
    const hasPartRefs: Array<{ "@id": string }> = [];
    if (featureItemList) {
        hasPartRefs.push({ "@id": featureListId });
    }
    if (benefitItemList) {
        hasPartRefs.push({ "@id": benefitListId });
    }
    const serviceOutputs = (benefits ?? [])
        .map(benefit => benefit.description)
        .filter(description => Boolean(description));
    const structuredDataGraph: Array<Record<string, unknown>> = [
        {
            "@type": "Organization",
            "@id": organizationId,
            "name": "Inventive HQ",
            "url": siteUrl,
            "logo": {
                "@type": "ImageObject",
                "url": toAbsoluteUrl('https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/dcb1dccb-de54-47b0-e5a8-a181e5319600/public')
            },
            "sameAs": [
                "https://www.facebook.com/inventivehq/",
                "https://x.com/inventivehq",
                "https://www.instagram.com/inventivehqllc/",
                "https://www.linkedin.com/company/inventivehq/",
                "https://www.youtube.com/@inventivehq"
            ],
            "contactPoint": [
                {
                    "@type": "ContactPoint",
                    "contactType": "sales",
                    "telephone": "+1-866-903-2097",
                    "email": "hello@inventivehq.com",
                    "areaServed": "US"
                }
            ],
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "2305 Historic Decatur Rd Suite 100",
                "addressLocality": "San Diego",
                "addressRegion": "CA",
                "postalCode": "92106",
                "addressCountry": "US"
            }
        },
        {
            "@type": "WebSite",
            "@id": websiteId,
            "url": siteUrl,
            "name": "Inventive HQ",
            "inLanguage": "en-US",
            "publisher": {
                "@id": organizationId
            }
        },
        {
            "@type": "WebPage",
            "@id": webPageId,
            "url": canonicalUrl,
            "name": pageTitle,
            "description": description,
            "isPartOf": {
                "@id": websiteId
            },
            "about": {
                "@id": serviceId
            },
            // Note: BreadcrumbList is rendered by the Breadcrumbs component to avoid duplicates
            "inLanguage": "en-US",
            ...(heroImageUrl && {
                "primaryImageOfPage": {
                    "@type": "ImageObject",
                    "url": heroImageUrl
                }
            })
        },
        {
            "@type": "Service",
            "@id": serviceId,
            "name": serviceName,
            "description": description,
            "serviceType": serviceName,
            "provider": {
                "@id": organizationId
            },
            "url": canonicalUrl,
            "areaServed": {
                "@type": "Country",
                "name": "United States"
            },
            "audience": {
                "@type": "BusinessAudience",
                "name": `${serviceName} Buyers`,
                "industry": categorySlug ?? "Professional Services"
            },
            "category": categorySlug ?? "Professional Services",
            ...(heroImageUrl && { "image": heroImageUrl }),
            ...(keywords && keywords.length > 0 && { "keywords": keywords }),
            ...(hasPartRefs.length > 0 && { "hasPart": hasPartRefs.length === 1 ? hasPartRefs[0] : hasPartRefs }),
            ...(serviceOutputs.length > 0 && { "serviceOutput": serviceOutputs }),
            ...(relatedServicesList && { "isRelatedTo": { "@id": relatedServicesId } }),
            ...(potentialActions.length > 0 && { "potentialAction": potentialActions })
        }
        // Note: BreadcrumbList is rendered by the Breadcrumbs component to avoid duplicates
    ];
    if (featureItemList) {
        structuredDataGraph.push(featureItemList);
    }
    if (benefitItemList) {
        structuredDataGraph.push(benefitItemList);
    }
    if (relatedServicesList) {
        structuredDataGraph.push(relatedServicesList);
    }
    if (faqItems && faqItems.length > 0) {
        structuredDataGraph.push({
            "@type": "FAQPage",
            "@id": faqId,
            "mainEntity": faqItems.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        });
    }
    const structuredData = {
        "@context": "https://schema.org",
        "@graph": structuredDataGraph
    };
    return (<>
      {/* Engagement Tracking */}
      <EngagementTracker contentType="service" slug={serviceSlug} />

      {/* Note: SEO meta tags are handled by generateMetadata in page.tsx (App Router pattern) */}

      {/* Preload hero image for LCP optimization - React 19 hoists to <head> */}
      {heroImage && isCloudflareImageUrl(heroImage) && (
        <link
          rel="preload"
          as="image"
          href={cfImage(heroImage, { width: 1920, quality: 80 })}
          imageSrcSet={cfImageSrcSet(heroImage, 'fullscreen')}
          imageSizes={SIZES.fullscreen}
        />
      )}

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData)
        }}/>

      <div className="min-h-screen bg-white dark:bg-gray-800">
        {/* Hero Section */}
        {showHero && (
        <section className={`relative text-white py-24 px-4 overflow-hidden ${!heroImage ? `bg-gradient-to-br ${heroBackgroundColor}` : ''}`}>
          {/* Hero background image - using img for LCP optimization */}
          {heroImage && (<>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={isCloudflareImageUrl(heroImage) ? cfImage(heroImage, { width: 1920, quality: 80 }) : heroImage}
              srcSet={isCloudflareImageUrl(heroImage) ? cfImageSrcSet(heroImage, 'fullscreen') : undefined}
              sizes={isCloudflareImageUrl(heroImage) ? SIZES.fullscreen : undefined}
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              loading="eager"
              decoding="sync"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-[#0b1120]" style={{ opacity: Math.max(0, Math.min(heroOverlayOpacity, 1)) }}></div>
          </>)}

          <div className="max-w-7xl mx-auto relative z-10">
            <Breadcrumbs items={breadcrumbs} className="mb-8 text-white opacity-90"/>

            <div className={heroContainerClasses}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {heroHeading}
              </h1>
              {heroSubheading && (<p className={`text-xl md:text-2xl mb-4 opacity-95${heroAlign === 'center' ? ' mx-auto max-w-3xl' : ''}`}>
                  {heroSubheading}
                </p>)}
              {heroBody && (<p className={heroBodyClasses}>
                  {heroBody}
                </p>)}
              {heroButtons && (heroButtons.primary || heroButtons.secondary) && (<div className={heroButtonsClasses}>
                  {heroButtons.primary && (<Link href={heroPrimaryHref ?? heroButtons.primary.href} className={heroButtons.primary.className
                    ? `${heroButtonBaseClasses} ${heroButtons.primary.className}`
                    : heroPrimaryDefaultClasses}>
                      {heroButtons.primary.text}
                    </Link>)}
                  {heroButtons.secondary && (<Link href={heroSecondaryHref ?? heroButtons.secondary.href} className={heroButtons.secondary.className
                    ? `${heroButtonBaseClasses} ${heroButtons.secondary.className}`
                    : heroSecondaryDefaultClasses}>
                      {heroButtons.secondary.text}
                    </Link>)}
                </div>)}
              {heroTrustBadges && heroTrustBadges.length > 0 && (<div className="mt-12">
                  <div className="rounded-3xl border border-white/15 bg-white/10 dark:bg-gray-800/10 px-6 py-5 backdrop-blur">
                    <div className={heroTrustLayoutClasses}>
                      {heroTrustLabel && (<span className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">{heroTrustLabel}</span>)}
                      <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-white/90 md:gap-6">
                        {heroTrustBadges.map((badge, index) => (<span key={`${badge.label}-${index}`} className="flex items-center gap-2">
                            {badge.icon ? (<Image src={badge.icon} alt={badge.label} width={16} height={16} className="h-4 w-4 object-contain" unoptimized/>) : (<ShieldCheck className="h-4 w-4 text-[#A7F3D0]"/>)}
                            <span>{badge.label}</span>
                          </span>))}
                        {heroTrustTrailingText && (<span className="text-white/60">{heroTrustTrailingText}</span>)}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>
        )}

        {/* Embedded Contact Form - Shows after hero for high-converting service pages */}
        {showEmbeddedContactForm && (
          <MiniContactForm
            serviceSlug={serviceSlug}
            serviceName={serviceName}
            categorySlug={categorySlug}
            {...embeddedContactFormProps}
          />
        )}

        {/* Before Content Section */}
        {beforeContent && (<div className="py-16 px-4">
            <div className="max-w-7xl mx-auto">
              {beforeContent}
            </div>
          </div>)}

        {/* Main Content */}
        <div className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </div>

        {/* Features Section */}
        {features && features.length > 0 && (<section className={featuresSectionClassName ?? "py-20 px-4 bg-gray-50 dark:bg-gray-800"}>
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-4" id="key-features">
                  Key Features
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {features.map((feature, index) => {
                let iconContent: ReactNode = null;
                if (feature.icon) {
                    if (typeof feature.icon === 'string') {
                        const trimmedIcon = feature.icon.trim();
                        if (trimmedIcon.startsWith('/') || trimmedIcon.startsWith('http')) {
                            const iconClassName = `h-6 w-6 object-contain ${feature.iconClassName ?? ''}`.trim();
                            iconContent = (<Image src={trimmedIcon} alt={feature.title} width={24} height={24} className={iconClassName} unoptimized/>);
                        }
                        else {
                            iconContent = (<span className={`text-sm font-semibold text-[#2813e8] ${feature.iconClassName ?? ''}`.trim()}>
                                {trimmedIcon}
                              </span>);
                        }
                    }
                    else if (isValidElement(feature.icon)) {
                        const iconElement = feature.icon as ReactElement<{ className?: string }>;
                        const existingClassName = iconElement.props.className ?? '';
                        const mergedClassName = `${existingClassName} ${feature.iconClassName ?? ''}`.trim();
                        iconContent = mergedClassName
                            ? cloneElement(iconElement, { className: mergedClassName })
                            : iconElement;
                    }
                    else {
                        iconContent = feature.icon;
                    }
                }
                return (<ScrollReveal key={index} animation="fade-up" delay={index * 80}>
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-shadow">
                      {iconContent && (<div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl ${feature.iconBackground ?? 'bg-[#2813e8]/10'}`}>
                          {iconContent}
                        </div>)}
                      <h3 className="text-xl font-bold text-[#4B4F58] mb-3">
                        {feature.title}
                      </h3>
                      <div className="text-gray-600 dark:text-gray-400">
                        {feature.description}
                      </div>
                    </div>
                  </ScrollReveal>);
            })}
              </div>
            </div>
          </section>)}

        {/* Benefits Section */}
        {benefits && benefits.length > 0 && (<section className="py-20 px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-4">
                  Why Choose Our {serviceName} Service?
                </h2>
              </div>

              <div className="max-w-4xl mx-auto space-y-4">
                {benefits.map((benefit, index) => (<ScrollReveal key={index} animation="fade-up" delay={index * 60}>
                    <div className="flex items-start space-x-4 p-5 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#2813e8] text-white rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#4B4F58] mb-2">
                          {benefit.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </ScrollReveal>))}
              </div>
            </div>
          </section>)}

        {/* After Content Section */}
        {afterContent && (<div className="py-16 px-4 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto">
              {afterContent}
            </div>
          </div>)}
        {/* CTA Section */}
        {showCTA && (<section className="bg-gradient-to-br from-[#2cc3d1] to-[#2813e8] text-white py-20 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {finalCTA.title}
              </h2>
              <p className="text-lg mb-8 opacity-90">
                {finalCTA.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                {finalCTA.primaryButton && (<Link href={ctaPrimaryHref ?? finalCTA.primaryButton.href} className={finalCTA.primaryButton.className
                    ? `${ctaButtonBaseClasses} ${finalCTA.primaryButton.className}`
                    : ctaPrimaryDefaultClasses}>
                    {finalCTA.primaryButton.text}
                  </Link>)}
                {finalCTA.secondaryButton && (<Link href={ctaSecondaryHref ?? finalCTA.secondaryButton.href} className={finalCTA.secondaryButton.className
                    ? `${ctaButtonBaseClasses} ${finalCTA.secondaryButton.className}`
                    : ctaSecondaryDefaultClasses}>
                    {finalCTA.secondaryButton.text}
                  </Link>)}
              </div>
              {/* Trust badges */}
              <TrustBadges variant="inline" className="justify-center text-white/90" />
            </div>
          </section>)}

        {/* FAQ Section */}
        {faqItems && faqItems.length > 0 && (<div className="py-20 px-4 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto">
              <FAQSection items={faqItems} toolName={serviceName}/>
            </div>
          </div>)}

        {/* Related Services */}
        {showRelatedServices && relatedServices && relatedServices.length > 0 && (<section className="py-20 px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-2" id="related-services">
                  Related Services
                </h2>
                <p className="text-gray-600 dark:text-gray-400">Explore our other solutions</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedServices.map((service, index) => (<Link key={index} href={`/services/${service.slug}`} className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-[#2813e8] hover:shadow-xl transition-all duration-200 group">
                    {service.icon && (<div className="mb-4">
                        <Image src={service.icon} alt={service.name} width={64} height={64} className="w-16 h-16 object-contain" unoptimized/>
                      </div>)}
                    <h3 className="text-xl font-bold mb-3 text-[#4B4F58] group-hover:text-[#2813e8] transition-colors">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <div className="text-[#2813e8] text-sm font-semibold flex items-center">
                      Learn more
                      <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </Link>))}
              </div>
            </div>
          </section>)}
      </div>

      {/* Sticky Mobile CTA */}
      <StickyMobileCTA
        text="Get Free Assessment"
        href={serviceContactUrl}
        showOnDesktop={false}
      />
    </>);
}
// Export types for use in service pages
export type { ServiceLayoutProps, ServiceFeature, ServiceBenefit, ServiceCTA, RelatedService };
